import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { business, images } from '../data/business';
import { sectors } from '../data/services';
import { ContactBand } from '../components/ContactBand';
import { PageIntro } from '../components/PageIntro';
import { useSeo } from '../utils/seo';
export function About() {
  useSeo({
    title: 'Over Goelst International B.V. — gordijnrails uit Amersfoort',
    description: 'Goelst International ontwikkelt sinds 1978 gordijnrailsystemen in Amersfoort. Lees over onze focus op technische precisie, esthetische integratie en professionele toepassingen.',
    path: '/over-ons'
  });
  return <>
      <PageIntro breadcrumb="Over ons" h1="Sinds 1978 gespecialiseerd in gordijnrailsystemen" lead="Goelst International B.V. ontwikkelt en produceert hoogwaardige gordijnrailsystemen voor architecten, interieurontwerpers en professionele projecten. Vanuit onze vestiging in Amersfoort bedienen wij klanten en projectpartners in Nederland en wereldwijd." meta={`${business.address.street}, ${business.address.postalCode} ${business.address.city} · KVK ${business.kvk}`} data-id="element-121" data-mp="2x8oldexr31on" />

      <section className="border-b border-line bg-white" aria-labelledby="bedrijfsprofiel" data-id="element-122" data-mp="23iq9rnr8f4vi">
        <div className="mx-auto max-w-shell px-6 py-16 md:py-24" data-id="element-123" data-mp="1yivhbfrt678c">
          <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16" data-id="element-124" data-mp="1ylq43dyao44x">
            <div className="space-y-6 text-[17px] leading-relaxed text-ink-soft" data-id="element-125" data-mp="2detduz5n9dl3">
              <h2 id="bedrijfsprofiel" className="font-display text-3xl font-700 leading-[1.1] tracking-tightest text-ink md:text-[40px]" data-id="element-126" data-mp="28grq40x9wq3a">
                Een bedrijf rond één onderdeel van het interieur
              </h2>
              <p data-id="element-127" data-mp="2w6cdqx4dgbiw">
                Er zijn bedrijven die gordijnrails leveren omdat het bij het assortiment hoort. Goelst International
                bestaat er om. Dat verschil bepaalt hoe wij werken: alle aandacht gaat naar de rail, het profiel, de
                loop, de aandrijving en de manier waarop dat geheel in een plafond, koof of wand verdwijnt.
              </p>
              <p data-id="element-128" data-mp="1xvb3cobylkyh">
                Ons assortiment omvat handbediende en elektrische gordijnrails, systemen voor zorginstellingen,
                commerciële toepassingen en volledig op maat gemaakte oplossingen. Die vier categorieën lopen in de
                praktijk vaak door elkaar. Een zorginstelling wil in de entree een commerciële uitstraling en op de
                afdeling een systeem dat tegen dagelijks gebruik kan. Een hotel vraagt in de suites gemotoriseerde
                rails en in de gangen een eenvoudige handbediende baan. Omdat alles uit dezelfde productfamilie komt,
                blijft het beeld consistent.
              </p>
              <p data-id="element-129" data-mp="m9cjzbhbe872">
                Sinds {business.since} combineert Goelst technische precisie met esthetische integratie. Die twee
                woorden dekken exact de spanning in ons werk. Technische precisie gaat over maatvoering, over de
                loopeigenschappen van glijders, over de belasting van een lange baan met zware verduisteringsstof en
                over de akoestiek van een motor in een stille kamer. Esthetische integratie gaat over het
                tegenovergestelde: dat je van al die techniek in de afgewerkte ruimte niets terugziet.
              </p>
              <p data-id="element-130" data-mp="12kwe4fxiitz4">
                Wij werken voor projecten in hospitality, healthcare, architectuur en high-end residential. Dat zijn
                omgevingen waarin ontwerpers keuzes maken die tot in het detail worden doorgevoerd, en waarin een
                zichtbare beugel of een hoorbare motor een ontwerp kan ondermijnen. Vanuit Amersfoort leveren wij
                zowel aan Nederlandse projecten als aan opdrachtgevers en projectpartners elders in de wereld.
              </p>
            </div>
            <figure data-id="element-131" data-mp="39b446dkx72ig">
              <img src={images.interiorBeige} alt="Interieur met hoge beige gordijnen langs twee raampartijen en rustig strijklicht" className="h-80 w-full object-cover md:h-[520px]" width={1200} height={1200} data-id="element-132" data-mp="39f1naz7hzu2c" />
              <figcaption className="mt-3 text-[13px] text-ink-mute" data-id="element-133" data-mp="r3ynnkkfxga9">
                Illustratief interieurbeeld van plafondgemonteerde gordijnrails — geen specifiek Goelst-project.
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section className="border-b border-line bg-canvas" aria-labelledby="precisie" data-id="element-134" data-mp="1oss77f5ysqev">
        <div className="mx-auto max-w-shell px-6 py-16 md:py-24" data-id="element-135" data-mp="1at908pow3l45">
          <h2 id="precisie" className="max-w-3xl font-display text-3xl font-700 leading-[1.1] tracking-tightest text-ink md:text-[40px]" data-id="element-136" data-mp="1euuh0m4jojjm">
            Technische precisie en esthetische integratie in de praktijk
          </h2>
          <div className="mt-12 grid gap-12 md:grid-cols-2 lg:gap-16" data-id="element-137" data-mp="3dvvl4gj0abn">
            <div className="space-y-5 text-[17px] leading-relaxed text-ink-soft" data-id="element-138" data-mp="3s0it81qmal05">
              <h3 className="font-display text-[22px] font-600 text-ink" data-id="element-139" data-mp="3n1z7a1c0x97s">Wat precisie hier betekent</h3>
              <p data-id="element-140" data-mp="5tkk34rc5hi9">
                Een gordijn dat niet soepel loopt, wordt harder aangetrokken. Daardoor verslijten glijders sneller,
                komt er zijwaartse belasting op de rail en raakt de stof beschadigd. De oplossing zit niet in een
                zwaardere rail, maar in de juiste combinatie van profiel, glijder en ophanging bij een bepaald
                stofgewicht en een bepaalde baanlengte.
              </p>
              <p data-id="element-141" data-mp="3j3i8c52rb51g">
                Bij elektrische en gemotoriseerde uitvoeringen komt daar de aandrijving bij. Wij dimensioneren de
                motor met marge, zodat een systeem niet continu op de grens van zijn capaciteit werkt. Dat is
                bepalend voor het geluidsniveau en voor de betrouwbaarheid over meerdere jaren.
              </p>
              <p data-id="element-142" data-mp="3b943u2c2655g">
                Maatvoering is de laatste, onvergeeflijke factor. Op de bouw is er geen ruimte om een rail nog bij te
                werken. Daarom leggen we lengtes, bochten en sparingen vast op tekening voordat er geproduceerd
                wordt.
              </p>
            </div>
            <div className="space-y-5 text-[17px] leading-relaxed text-ink-soft" data-id="element-143" data-mp="3p08uiexg7n3n">
              <h3 className="font-display text-[22px] font-600 text-ink" data-id="element-144" data-mp="2sc2wiav17syc">Wat integratie hier betekent</h3>
              <p data-id="element-145" data-mp="2wdxvv9tejw9h">
                In veel hedendaagse ontwerpen mag er tussen plafond en gordijn niets zichtbaar zijn. Dat vraagt om
                inbouw gordijnrails in een koof of sparing, of om plafond gordijnrails met een profiel dat zo smal is
                dat het optisch verdwijnt. De consequenties daarvan zitten in het bouwkundig detail en moeten dus
                vroeg in het proces worden besproken.
              </p>
              <p data-id="element-146" data-mp="lmy18w82i707">
                Integratie betekent ook dat de bediening logisch is. Waar zit de schakelaar, hoe ver openen de
                gordijnen, bewegen ze samen of afzonderlijk, en wat gebeurt er als een gast of medewerker toch met de
                hand duwt? Dat zijn ontwerpvragen, geen montagevragen.
              </p>
              <p data-id="element-147" data-mp="1re9bfcotf6hs">
                Tot slot hoort onderhoud bij de integratie. Motoren, besturing en ophanging blijven bereikbaar, zodat
                een systeem later opnieuw ingeregeld of geserviced kan worden zonder dat het plafond open moet.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-line bg-white" aria-labelledby="sectoren" data-id="element-148" data-mp="1df9nk794mvmo">
        <div className="mx-auto max-w-shell px-6 py-16 md:py-24" data-id="element-149" data-mp="18fx4lxldm8pd">
          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16" data-id="element-150" data-mp="37s9bcnnnuw51">
            <div data-id="element-151" data-mp="37rgag3bdv0ut">
              <h2 id="sectoren" className="font-display text-3xl font-700 leading-[1.1] tracking-tightest text-ink md:text-[40px]" data-id="element-152" data-mp="37pvficl5pfwf">
                Professionele toepassingen
              </h2>
              <p className="mt-5 text-[17px] leading-relaxed text-ink-soft" data-id="element-153" data-mp="2glu6rpuygmhi">
                Onze systemen worden toegepast in vier soorten omgevingen. De techniek overlapt; de eisen verschillen
                sterk.
              </p>
              <figure className="mt-10" data-id="element-154" data-mp="26v98k3j71jz4">
                <img src={images.railMulti} alt="Meerbaans aluminium gordijnrailsysteem met gebogen uiteinden" className="w-full bg-canvas object-contain p-8" width={1000} height={1000} data-id="element-155" data-mp="1swrsmrzj5brr" />
                <figcaption className="mt-2 text-[13px] text-ink-mute" data-id="element-156" data-mp="1nxyaund7myls">
                  Productbeeld van een meerbaans railopstelling.
                </figcaption>
              </figure>
            </div>
            <dl className="divide-y divide-line" data-id="element-157" data-mp="3icr8erxnlzgd">
              {sectors.map(sector => <div key={sector.title} className="py-7 first:pt-0" data-id="element-158" data-mp="16omb71oyw023">
                  <dt className="font-display text-[22px] font-600 text-ink" data-id="element-159" data-mp="16mt1fdj24zpv">{sector.title}</dt>
                  <dd className="mt-3 max-w-2xl text-[16px] leading-relaxed text-ink-soft" data-id="element-160" data-mp="12l7ltkf527bz">{sector.text}</dd>
                </div>)}
              <div className="py-7" data-id="element-161" data-mp="1bnqkdsbw5xsf">
                <dt className="font-display text-[22px] font-600 text-ink" data-id="element-162" data-mp="1bmnv1ou7w3em">Nederland en internationaal</dt>
                <dd className="mt-3 max-w-2xl text-[16px] leading-relaxed text-ink-soft" data-id="element-163" data-mp="1qb1z233ddxwr">
                  Vanuit Amersfoort bedienen wij klanten en projectpartners in Nederland en wereldwijd. De
                  uitgangspunten blijven daarbij hetzelfde: een systeem dat op tekening klopt, op de bouw past en na
                  jaren nog soepel loopt.
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <section className="border-b border-line bg-ink text-white" aria-labelledby="samenwerken" data-id="element-164" data-mp="18rnn5nx4yl">
        <div className="mx-auto max-w-shell px-6 py-16 md:py-24" data-id="element-165" data-mp="3ce9cvooeyhk1">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16" data-id="element-166" data-mp="37ewfi9zx1s37">
            <div data-id="element-167" data-mp="37dlciceaood5">
              <h2 id="samenwerken" className="font-display text-3xl font-700 leading-[1.1] tracking-tightest md:text-[40px]" data-id="element-168" data-mp="37h97lfze1itz">
                Met wie wij samenwerken
              </h2>
              <p className="mt-5 max-w-xl text-[17px] leading-relaxed text-white/75" data-id="element-169" data-mp="3v50jiih0ntuq">
                Onze gesprekspartners zitten meestal niet aan het einde van de keten. Hoe eerder wij in een project
                betrokken zijn, hoe eenvoudiger het detail wordt.
              </p>
            </div>
            <div className="space-y-8" data-id="element-170" data-mp="3hdm0wcr0tuo7">
              {[{
              title: 'Architecten',
              text: 'Voor het bouwkundig detail rond sparingen, koven en plafondafwerking, inclusief de gevolgen van een volledig ingebouwde rail.'
            }, {
              title: 'Interieurontwerpers',
              text: 'Voor de afstemming tussen stofkeuze, valhoogte, plooivorming en de zichtbaarheid van het railsysteem.'
            }, {
              title: 'Aannemers en installateurs',
              text: 'Voor maatvoering, voeding, kabelroutes en een levering die aansluit op de bouwplanning.'
            }, {
              title: 'Projectpartners en opdrachtgevers',
              text: 'Voor grotere series met repeterende kamertypes, waarbij specificatie en nalevering herleidbaar moeten blijven.'
            }].map(item => <div key={item.title} className="border-t border-white/15 pt-6" data-id="element-171" data-mp="1ivadqvtysh2t">
                  <h3 className="font-display text-[19px] font-600" data-id="element-172" data-mp="1ix3ddr65u11b">{item.title}</h3>
                  <p className="mt-2 max-w-xl text-[16px] leading-relaxed text-white/70" data-id="element-173" data-mp="1myowhu9afbjy">{item.text}</p>
                </div>)}
              <p className="pt-2" data-id="element-174" data-mp="3nytrne5ryihx">
                <Link to="/diensten" className="inline-flex items-center gap-2 text-[15px] font-semibold text-white underline decoration-accent underline-offset-4" data-id="element-175" data-mp="3nzwkvsqkkx10">
                  Bekijk per categorie wat wij leveren
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-176" data-mp="3sxy2084fqm9u" />
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-line bg-canvas" aria-labelledby="kerngegevens" data-id="element-177" data-mp="6ilptzyhbrxd">
        <div className="mx-auto max-w-shell px-6 py-16 md:py-20" data-id="element-178" data-mp="6ky86e85g4ay">
          <h2 id="kerngegevens" className="font-display text-3xl font-700 leading-[1.1] tracking-tightest text-ink md:text-[36px]" data-id="element-179" data-mp="khuvw6sz15nm">
            Kerngegevens
          </h2>
          <dl className="mt-10 grid gap-8 border-t border-line pt-10 sm:grid-cols-2 lg:grid-cols-4" data-id="element-180" data-mp="2m9zsvwpbut57">
            <div data-id="element-181" data-mp="2hchliaqyklzb">
              <dt className="text-[13px] uppercase tracking-[0.14em] text-ink-mute" data-id="element-182" data-mp="2hd0ydd1r4k6h">Bedrijfsnaam</dt>
              <dd className="mt-2 text-[17px] text-ink" data-id="element-183" data-mp="11rpbo9ne7d65">{business.name}</dd>
            </div>
            <div data-id="element-184" data-mp="11tjk4ohh9ctc">
              <dt className="text-[13px] uppercase tracking-[0.14em] text-ink-mute" data-id="element-185" data-mp="o0k5sptflond">Vestiging</dt>
              <dd className="mt-2 text-[17px] text-ink" data-id="element-186" data-mp="s1nmfejazopn">
                {business.address.street}
                <br data-id="element-187" data-mp="e2e52bjd2ok3" />
                {business.address.postalCode} {business.address.city}
              </dd>
            </div>
            <div data-id="element-188" data-mp="j28wqhayni67">
              <dt className="text-[13px] uppercase tracking-[0.14em] text-ink-mute" data-id="element-189" data-mp="j2she5rzwknx">KVK-nummer</dt>
              <dd className="mt-2 text-[17px] text-ink" data-id="element-190" data-mp="530uffji3txs">{business.kvk}</dd>
            </div>
            <div data-id="element-191" data-mp="93t76irl0gyt">
              <dt className="text-[13px] uppercase tracking-[0.14em] text-ink-mute" data-id="element-192" data-mp="93bbbn8u04rf">Officiële website</dt>
              <dd className="mt-2 text-[17px]" data-id="element-193" data-mp="1zdcvlv2nw30s">
                <a href={business.website} target="_blank" rel="noopener" className="text-ink underline decoration-accent underline-offset-4" data-id="element-194" data-mp="23hc9e5wk4nqg">
                  {business.websiteLabel}
                </a>
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <ContactBand heading="Overleggen over een railsysteem voor uw project?" text="Wij denken graag mee vanaf de ontwerpfase. Neem contact op met ons kantoor in Amersfoort; op werkdagen zijn wij tussen 09:00 en 18:00 bereikbaar." data-id="element-195" data-mp="zt5szsy5lzx7" />
    </>;
}